import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";
import { Booking, Resort, resortArray } from "@/types";
import { createBookings, createCustomBookings } from "@/api/bookings";
import { Loader2 } from "lucide-react";

interface CustomBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (booking: Omit<Booking, '_id'>) => void;
  contractId: number;
}

export function CustomBookingModal({ isOpen, onClose, onSubmit, contractId }: CustomBookingModalProps) {
  const [resort, setResort] = useState<Resort | "">("");
  const [unitNumber, setUnitNumber] = useState("");
  const [unitType, setUnitType] = useState("");
  const [week, setWeek] = useState<number | "">("");
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => currentYear + i);
  const maxSelections = 20;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (hasSubmitted) {
      return; // Prevent multiple submissions
    }

    if (!resort || !unitNumber || !unitType || !week || selectedYears.length === 0) {
      toast.error("All fields are required");
      return;
    }

    // Validate week is between 1 and 53
    const weekNumber = Number(week);
    if (isNaN(weekNumber) || weekNumber < 1 || weekNumber > 53) {
      toast.error("Week must be a number between 1 and 53");
      return;
    }

    setIsLoading(true);
    setHasSubmitted(true);

    try {
      const booking = {
        contractId,
        resort,
        unitNumber,
        week: weekNumber,
        years: selectedYears,
        type: unitType,
        status: 1,
        isCustom: true
      };

      // Call the onSubmit callback
      await onSubmit(booking);

      // Reset form and close modal
      setResort("");
      setUnitNumber("");
      setUnitType("");
      setWeek("");
      setSelectedYears([]);
      setSelectAll(false);
      onClose(); // Explicitly close the modal after successful submission
    } catch (error: any) {
      toast.error(error.message || "Failed to add custom booking");
    } finally {
      setIsLoading(false);
      setHasSubmitted(false);
    }
  };

  const handleSelectAllYears = (checked: boolean) => {
    setSelectAll(checked);
    setSelectedYears(checked ? years.slice(0, maxSelections) : []);
  };

  const handleYearToggle = (year: number) => {
    setSelectedYears(prev =>
      prev.includes(year)
        ? prev.filter(y => y !== year)
        : [...prev, year].slice(0, maxSelections)
    );
  };

  // Reset hasSubmitted when the modal closes
  const handleCloseModal = () => {
    setHasSubmitted(false);
    setResort(""); // Reset resort
    setUnitNumber(""); // Reset unit number
    setUnitType(""); // Reset unit type
    setWeek(""); // Reset week
    setSelectedYears([]); // Reset selected years
    setSelectAll(false); // Reset select all
    onClose(); // Call the onClose function to close the modal
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCloseModal}>
      <DialogContent className="sm:max-w-[600px] bg-background">
        <DialogHeader>
          <DialogTitle>Add Custom Booking</DialogTitle>
          <DialogDescription>
            Enter the details for the custom booking. You can select multiple years.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="resort">Resort</Label>
            <Select value={resort || ""} onValueChange={(value) => setResort(value as Resort)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a resort" />
              </SelectTrigger>
              <SelectContent>
                {resortArray.map((resortName) => (
                  <SelectItem key={resortName} value={resortName}>
                    {resortName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="unitNumber">Unit Number</Label>
              <Input
                id="unitNumber"
                value={unitNumber}
                onChange={(e) => setUnitNumber(e.target.value)}
                placeholder="Enter unit number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unitType">Unit Type</Label>
              <Input
                id="unitType"
                value={unitType}
                onChange={(e) => setUnitType(e.target.value)}
                placeholder="Enter unit type"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="week">Week (1-53)</Label>
              <Input
                id="week"
                type="number"
                min="1"
                max="53"
                value={week}
                onChange={(e) => setWeek(parseInt(e.target.value) || "")}
                placeholder="Enter week number"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Years (Select up to {maxSelections})</Label>
            <ScrollArea className="h-[200px] rounded-md border p-4">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="select-all"
                    checked={selectAll}
                    onCheckedChange={(checked) => handleSelectAllYears(checked as boolean)}
                  />
                  <label
                    htmlFor="select-all"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Select All (up to {maxSelections} years)
                  </label>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {years.map((year) => (
                    <div key={year} className="flex items-center space-x-2">
                      <Checkbox
                        id={`year-${year}`}
                        checked={selectedYears.includes(year)}
                        onCheckedChange={() => handleYearToggle(year)}
                      />
                      <label
                        htmlFor={`year-${year}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {year}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={selectedYears.length === 0 || !week || isLoading || hasSubmitted}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add Booking"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}